import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, Home as HomeIcon, ChevronRight } from 'lucide-react';
import { getProducts, getCategories } from '../lib/api';
import ProductCard from '../components/ProductCard';

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const search = params.get('search') || '';
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (search) q.search = search;
    getProducts(q).then(list => { setProducts(list); setLoading(false); }).catch(() => setLoading(false));
  }, [category, search]);

  const sorted = [...products].sort((a, b) => {
    if (sort === 'low') return a.price - b.price;
    if (sort === 'high') return b.price - a.price;
    if (sort === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  const selectCat = (name) => {
    const p = new URLSearchParams(params);
    if (name) p.set('category', name); else p.delete('category');
    p.delete('search');
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-[#0f7a37] flex items-center gap-1"><HomeIcon size={14} /> Home</Link>
        <ChevronRight size={14} />
        <span className="text-[#14181b] font-medium">{category || (search ? `Search: ${search}` : 'Shop')}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* sidebar */}
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-40">
            <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><SlidersHorizontal size={18} className="text-[#0f7a37]" /> Categories</h3>
            <button onClick={() => selectCat('')}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors ${!category ? 'bg-[#0f7a37] text-white font-semibold' : 'hover:bg-[#eaf6ec] text-gray-700'}`}>
              All Products
            </button>
            {cats.map(c => (
              <button key={c.slug} onClick={() => selectCat(c.name)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 flex items-center justify-between transition-colors ${category === c.name ? 'bg-[#0f7a37] text-white font-semibold' : 'hover:bg-[#eaf6ec] text-gray-700'}`}>
                <span>{c.name}</span>
                <span className={`text-xs ${category === c.name ? 'text-white/80' : 'text-gray-400'}`}>{c.count}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* products */}
        <main className="lg:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-head text-2xl md:text-3xl font-extrabold text-[#14181b]">{category || (search ? 'Search Results' : 'All Products')}</h1>
              <p className="text-sm text-gray-500 mt-1">{sorted.length} products found</p>
            </div>
            <select value={sort} onChange={e => setSort(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0f7a37]">
              <option value="default">Sort: Featured</option>
              <option value="low">Price: Low to High</option>
              <option value="high">Price: High to Low</option>
              <option value="name">Name: A-Z</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl animate-pulse aspect-[3/4]" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No products found.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {sorted.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Shop;
